import { BODY_FONT, DISPLAY_FONT, CARD_BORDER_RADIUS } from 'src/constants/ui';
import { RightChevronIcon } from 'src/icons';
import { PopoverOrigin, Theme } from '@mui/material';
import {
    createTheme,
    getActionPalette,
    getFloatingSurfaceStyles,
    getInterfacePalette,
} from 'src/lib/style/theme';
import palette, { Color } from './constants/palette';

const actionPalette = getActionPalette('light');
const interfacePalette = getInterfacePalette('light');

export const baseTheme = createTheme({
    palette: {
        primary: actionPalette.primary.mui,
        secondary: actionPalette.secondary.mui,
        // custom colors provided via resqPalette
        success: {
            main: interfacePalette.surface.success,
            contrastText: Color.White,
        },
        warning: {
            main: interfacePalette.text.warning,
            contrastText: Color.White,
        },
        info: {
            main: interfacePalette.text.link,
            contrastText: Color.White,
        },
        // neutral removed; use resqPalette instead
        error: {
            main: interfacePalette.surface.critical,
            contrastText: Color.White,
        },
        // payment removed from MUI palette; use resqPalette if needed
        text: {
            primary: palette.textPrimary,
            secondary: palette.textSecondary,
        },
        divider: interfacePalette.divider.default,
        background: {
            default: palette.background,
        },
    },
    typography: {
        button: { fontFamily: DISPLAY_FONT },
        fontFamily: BODY_FONT,
        h1: {
            fontFamily: DISPLAY_FONT,
            fontSize: '24px',
            fontWeight: 600,
            color: palette.textPrimary,
        },
        h2: {
            fontFamily: DISPLAY_FONT,
            fontSize: '20px',
            fontWeight: 600,
            color: palette.textPrimary,
        },
        h3: {
            fontFamily: DISPLAY_FONT,
            fontSize: '18px',
            fontWeight: 600,
            color: palette.textPrimary,
            lineHeight: '1.5',
        },
        h4: {
            fontFamily: DISPLAY_FONT,
            fontSize: '16px',
            fontWeight: 600,
            color: palette.textPrimary,
            lineHeight: '1.5',
        },
        h5: {
            fontFamily: DISPLAY_FONT,
            fontSize: '14px',
            fontWeight: 500,
            color: palette.textPrimary,
            lineHeight: '1.5',
        },
        subtitle1: {
            fontSize: '16px',
            fontWeight: 400,
            color: palette.textSecondary,
            lineHeight: '1.5',
        },
        subtitle2: {
            fontSize: '14px',
            fontWeight: 400,
            color: palette.textSecondary,
            lineHeight: '1.5',
        },
        caption: {
            fontSize: '13px',
            fontWeight: 400,
            color: palette.textSecondary,
            lineHeight: '1.5',
        },
        body1: {
            fontSize: '16px',
            fontWeight: 400,
            color: palette.textPrimary,
            lineHeight: '1.5',
        },
        body2: {
            fontSize: '14px',
            fontWeight: 400,
            color: palette.textPrimary,
            lineHeight: '1.5',
        },
        body3: {
            display: 'block',
            fontSize: '13px',
            fontWeight: 400,
            color: palette.textSecondary,
            lineHeight: '1.3',
        },
    },
});

/**
 * Resolves a style override against the theme rendering it, so each dashboard theme (light or
 * dark) gets its own resqPalette tokens instead of the light base values baked in here.
 */
const fromTheme =
    <Styles,>(styles: (theme: Theme) => Styles) =>
    ({ theme }: { theme: unknown }) =>
        styles(theme as Theme);

baseTheme.components = {
    MuiDialog: {
        defaultProps: {
            fullWidth: true,
        },
        styleOverrides: {
            paper: {
                [baseTheme.breakpoints.down('sm')]: {
                    '&:not(.MuiDialog-paperFullScreen)': {
                        margin: baseTheme.spacing(2),
                        maxWidth: `calc(100% - ${baseTheme.spacing(4)})`,
                        maxHeight: `calc(100% - ${baseTheme.spacing(4)})`,
                        '&.MuiDialog-paperFullWidth': {
                            width: `calc(100% - ${baseTheme.spacing(4)})`,
                        },
                    },
                },
            },
        },
    },
    MuiCollapse: {
        defaultProps: {
            timeout: 375,
        },
    },
    MuiAppBar: {
        styleOverrides: {
            root: fromTheme((theme) => ({
                colorPrimary: {
                    color: theme.resqPalette.text.default,
                },
            })),
        },
    },
    MuiBreadcrumbs: {
        defaultProps: {
            separator: <RightChevronIcon height="24px" />,
        },
        styleOverrides: {
            li: fromTheme((theme) => ({
                '&:last-child': {
                    color: theme.resqPalette.text.default,
                },
                '& p': {
                    fontWeight: 600,
                },
                '& svg': {
                    marginRight: theme.spacing(1.5),
                },
            })),
            root: fromTheme((theme) => ({
                marginBottom: theme.spacing(4),
                fontWeight: 600,
                '& a': {
                    color: theme.resqPalette.text.link,
                    '&:hover': {
                        textDecoration: 'underline',
                    },
                },
            })),
            separator: {
                marginRight: baseTheme.spacing(0.5),
                marginLeft: baseTheme.spacing(0.5),
            },
        },
    },
    MuiSwitch: {
        styleOverrides: {
            root: fromTheme((theme) => ({
                '& .MuiSwitch-track': {
                    borderRadius: 12,
                    width: 'calc(100% + 10px)',
                    flexShrink: 0,
                    marginLeft: -5,
                    backgroundColor: theme.resqPalette.toggle.track,
                    border: `1px solid ${theme.resqPalette.toggle.trackBorder}`,
                    boxSizing: 'border-box',
                    opacity: 1,
                    height: 24,
                    marginTop: -5,
                },
                '& .MuiSwitch-thumb': {
                    boxShadow: 'none',
                },
                '& .MuiSwitch-switchBase': {
                    color: theme.resqPalette.toggle.thumb,
                    '&:hover, &.Mui-checked:hover': {
                        backgroundColor: 'transparent',
                    },
                    '@media (hover: hover)': {
                        '&:hover + .MuiSwitch-track': {
                            backgroundColor: theme.resqPalette.toggle.trackHovered,
                        },
                        '&.Mui-checked:hover + .MuiSwitch-track': {
                            backgroundColor: theme.resqPalette.toggle.checkedTrackHovered,
                        },
                    },
                    '&.Mui-checked': {
                        color: theme.resqPalette.toggle.thumb,
                        '& + .MuiSwitch-track': {
                            backgroundColor: theme.resqPalette.toggle.checkedTrack,
                            borderColor: theme.resqPalette.toggle.checkedTrack,
                            opacity: 1,
                        },
                    },
                    '&.Mui-focusVisible .MuiSwitch-thumb': {
                        outline: `2px solid ${theme.resqPalette.border.interactive}`,
                        outlineOffset: 3,
                    },
                    '&.Mui-disabled, &.Mui-checked.Mui-disabled': {
                        color: theme.resqPalette.text.disabled,
                        '& + .MuiSwitch-track': {
                            backgroundColor: theme.resqPalette.surface.disabled,
                            borderColor: theme.resqPalette.surface.disabled,
                            opacity: 1,
                        },
                    },
                },
                '&.MuiSwitch-sizeSmall .MuiSwitch-track': {
                    height: 20,
                },
            })),
        },
    },
    MuiButton: {
        defaultProps: {
            color: 'neutral',
        },
        styleOverrides: {
            contained: {
                boxShadow: 'none',
            },
            root: fromTheme((theme) => ({
                fontFamily: DISPLAY_FONT,
                textTransform: 'unset',
                transitionProperty: 'background-color, box-shadow, border, color',
                whiteSpace: 'nowrap',
                '&.MuiButton-textNeutral': {
                    color: theme.resqPalette.text.default,
                },
                '&.MuiButton-outlinedNeutral': {
                    color: theme.resqPalette.text.default,
                },
            })),
        },
    },
    MuiAlert: {
        styleOverrides: {
            standardInfo: fromTheme((theme) => ({
                backgroundColor: theme.resqPalette.surface.highlightSubdued,
                border: `1px solid ${theme.resqPalette.border.highlight}`,
                color: theme.resqPalette.text.default,
                '.MuiAlert-icon': {
                    color: theme.resqPalette.icon.highlight,
                },
            })),
            standardError: fromTheme((theme) => ({
                backgroundColor: theme.resqPalette.surface.criticalSubdued,
                border: `1px solid ${theme.resqPalette.border.critical}`,
                '.MuiAlert-icon': {
                    color: theme.resqPalette.icon.critical,
                },
            })),
            standardWarning: fromTheme((theme) => ({
                backgroundColor: theme.resqPalette.surface.warningSubdued,
                border: `1px solid ${theme.resqPalette.border.warning}`,
                '.MuiAlert-icon': {
                    color: theme.resqPalette.icon.warning,
                },
            })),
            root: {
                paddingTop: 0,
                paddingBottom: 0,
                '.MuiButton-textNeutral': {
                    marginTop: baseTheme.spacing(1),
                    color: `${palette.warningHiglightBrown} !important`,
                    borderColor: palette.warningHiglightBrown,
                    boxShadow: 'none',
                    '&:hover': {
                        borderColor: palette.warningHiglightBrown,
                    },
                },
                '.MuiButton-outlinedNeutral': {
                    marginTop: baseTheme.spacing(1),
                    color: `${palette.warningHiglightBrown} !important`,
                    borderColor: palette.warningHiglightBrown,
                    boxShadow: 'none',
                    '&:hover': {
                        borderColor: palette.warningHiglightBrown,
                    },
                },
            },
        },
    },
    MuiButtonGroup: {
        styleOverrides: {
            contained: {
                boxShadow: 'none',
            },
        },
    },

    MuiFormLabel: {
        styleOverrides: {
            root: fromTheme((theme) => ({
                color: theme.resqPalette.text.default,
            })),
        },
    },
    MuiInput: {
        styleOverrides: {
            underline: {
                '&.Mui-disabled::before': {
                    borderBottomColor: 'transparent',
                    borderBottomStyle: 'solid',
                },
            },
        },
    },
    MuiInputAdornment: {
        styleOverrides: {
            positionStart: {
                marginRight: 0,
            },
        },
    },
    MuiInputBase: {
        styleOverrides: {
            input: fromTheme((theme) => ({
                '&.Mui-disabled': {
                    color: theme.resqPalette.text.disabled,
                    WebkitTextFillColor: theme.resqPalette.text.disabled,
                    opacity: 1,
                },
                '&::placeholder': {
                    color: theme.resqPalette.text.placeHolder,
                    WebkitTextFillColor: theme.resqPalette.text.placeHolder,
                    opacity: 1,
                },
                '&::-moz-placeholder': {
                    color: theme.resqPalette.text.placeHolder,
                    opacity: 1,
                },
                '&:-ms-input-placeholder': {
                    color: theme.resqPalette.text.placeHolder,
                    opacity: 1,
                },
                '&::-webkit-input-placeholder': {
                    color: theme.resqPalette.text.placeHolder,
                    WebkitTextFillColor: theme.resqPalette.text.placeHolder,
                    opacity: 1,
                },
            })),
            root: {
                // keep default disabled color; adornments hidden by opacity
                '&.Mui-disabled .MuiSelect-icon, &.Mui-disabled .MuiAutocomplete-endAdornment': {
                    opacity: 0,
                },
            },
        },
    },
    MuiFormControl: {
        defaultProps: {
            variant: 'standard',
        },
    },
    MuiTextField: {
        defaultProps: {
            variant: 'standard',
            fullWidth: true,
        },
    },
    MuiOutlinedInput: {
        defaultProps: {
            notched: false,
            label: null,
        },
        styleOverrides: {
            root: fromTheme((theme) => ({
                '& [disabled]': {
                    backgroundColor: theme.resqPalette.surface.disabled,
                    color: theme.resqPalette.text.disabled,
                    WebkitTextFillColor: theme.resqPalette.text.disabled,
                },
            })),
        },
    },
    MuiInputLabel: {
        defaultProps: {
            shrink: true,
        },
        styleOverrides: {
            root: fromTheme((theme) => ({
                color: theme.resqPalette.text.default,
            })),
        },
    },
    MuiPaper: {
        styleOverrides: {
            root: {
                boxShadow: 'none',
            },
            rounded: {
                borderRadius: CARD_BORDER_RADIUS,
                overflow: 'hidden',
            },
        },
    },
    // Menus and Select lists render inside Popover paper.
    MuiPopover: {
        styleOverrides: {
            paper: fromTheme(getFloatingSurfaceStyles),
        },
    },
    MuiAutocomplete: {
        styleOverrides: {
            paper: fromTheme(getFloatingSurfaceStyles),
        },
    },
    MuiSelect: {
        defaultProps: {
            variant: 'standard',
            MenuProps: {
                anchorOrigin: {
                    vertical: 'bottom' as PopoverOrigin['vertical'],
                    horizontal: 'center' as PopoverOrigin['horizontal'],
                },
                transformOrigin: {
                    vertical: 'top',
                    horizontal: 'center',
                },
            },
        },
        styleOverrides: {
            icon: {
                width: '0.85em',
                height: '0.85em',
                top: 'calc(50% - 10px)',
            },
            select: fromTheme((theme) => ({
                '&.Mui-disabled': {
                    userSelect: 'auto',
                    backgroundColor: theme.resqPalette.surface.disabled,
                    color: theme.resqPalette.text.disabled,
                },
            })),
        },
    },
    MuiSvgIcon: {
        styleOverrides: {
            fontSizeLarge: {
                fontSize: 28,
            },
        },
    },
    MuiListItemText: {
        styleOverrides: {
            root: {
                marginTop: baseTheme.spacing(1),
                marginBottom: baseTheme.spacing(1),
            },
        },
    },
    MuiTab: {
        styleOverrides: {
            root: fromTheme((theme) => ({
                fontFamily: DISPLAY_FONT,
                textTransform: 'unset',
                fontSize: '16px',
                fontWeight: 600,
                opacity: 1,
                color: theme.resqPalette.text.subdued,
                minWidth: 'unset !important',
                maxWidth: 'unset !important',
                minHeight: 'unset !important',
                lineHeight: 'unset !important',
                padding: theme.spacing(0, 0, 1),
                marginRight: theme.spacing(4),
                '&.Mui-focused': {
                    color: theme.resqPalette.text.default,
                },
                '&.MuiTab-textColorSecondary.Mui-selected': {
                    color: theme.resqPalette.text.default,
                },
            })),
        },
    },
    MuiTabs: {
        defaultProps: {
            indicatorColor: 'secondary',
            textColor: 'secondary',
        },

        styleOverrides: {
            indicator: {
                height: '3px',
            },
            root: {
                minHeight: 'unset',
            },
        },
    },
    MuiTable: {
        styleOverrides: {
            root: {
                borderCollapse: 'separate',
            },
        },
    },
    MuiTableCell: {
        styleOverrides: {
            head: fromTheme((theme) => ({
                backgroundColor: theme.resqPalette.surface.tableSubdued,
                color: theme.resqPalette.text.default,
                fontSize: '14px',
                fontWeight: 500,
                verticalAlign: 'bottom',
                lineHeight: 1.3,
            })),
            body: fromTheme((theme) => ({
                borderTop: `1px solid ${theme.resqPalette.divider.table}`,
            })),
            footer: fromTheme((theme) => ({
                borderTop: `1px solid ${theme.resqPalette.divider.table}`,
            })),
            root: {
                borderBottom: 'none',
                '& a': {
                    textDecoration: 'underline',
                },
                '&:first-of-type': {
                    paddingLeft: baseTheme.spacing(4),
                },
                '&:last-child': {
                    paddingRight: baseTheme.spacing(4),
                },
            },
        },
    },
    MuiTablePagination: {
        styleOverrides: {
            toolbar: {
                padding: baseTheme.spacing(0, 4),
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
            },
            spacer: {
                display: 'none',
            },
        },
    },
    MuiToolbar: {
        styleOverrides: {
            regular: {
                [baseTheme.breakpoints.up('sm')]: {
                    minHeight: '48px !important',
                },
            },
        },
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: {
                fontSize: '12px',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
            },
        },
    },
    MuiTypography: {
        styleOverrides: {
            body1: {
                '& a': {
                    textDecoration: 'underline',
                },
            },
        },
    },
    MuiDialogTitle: {
        styleOverrides: {
            root: {
                padding: baseTheme.spacing(3, 4),
                fontWeight: 600,
                fontSize: '1.375rem',
                [baseTheme.breakpoints.down('md')]: {
                    padding: baseTheme.spacing(4, 3, 3),
                },
            },
        },
    },
    MuiDialogContent: {
        styleOverrides: {
            root: {
                padding: baseTheme.spacing(4),
                [baseTheme.breakpoints.down('md')]: {
                    padding: baseTheme.spacing(4, 3),
                },
            },
        },
    },
    MuiDialogActions: {
        styleOverrides: {
            root: fromTheme((theme) => ({
                padding: theme.spacing(0, 4, 3),
                justifyContent: 'space-between',
                [theme.breakpoints.down('md')]: {
                    padding: theme.spacing(2, 3),
                    flexWrap: 'wrap',
                    gap: theme.spacing(1),
                    '& .MuiButton-root': { whiteSpace: 'normal', overflowWrap: 'anywhere' },
                    backgroundColor: theme.resqPalette.background.default,
                },
            })),
        },
    },
};
